import { useContext, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { ExtendedPatient } from "@thalamos/aspire-common";
import { isSoadUserSession } from "@thalamos/aspire-common";

import { WithPatient } from "../../hoc/WithPatient.js";
import { routeFns } from "../../routePaths.js";
import { BannerDialog } from "../FormProgressPage/helpers/BannerDialog.js";
import type { editPatientDialog } from "./PatientCreateEdit.js";
import { PatientCreateEdit } from "./PatientCreateEdit.js";
import { isValidPatientDemographics } from "~/components/util.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { MissingDemographicsDialog } from "~/modals/MissingDemographicsDialog/MissingDemographicsDialog.js";

type PatientEditProps = {
  mode?: editPatientDialog;
  refetchDraft?: () => void;
};

function PatientEditInner({
  mode,
  refetchDraft,
  patient,
  setShowUpdateSuccessDialog,
  redirectTo,
}: PatientEditProps & {
  patient: ExtendedPatient;
  setShowUpdateSuccessDialog: (value: boolean) => void;
  redirectTo?: string | null;
}) {
  const { patientId, formDraftId } = useParams();

  const userContext = useContext(LoggedInUserContext);
  const isSoad = isSoadUserSession(userContext?.user);

  const isPatientMissingDemographics = !isValidPatientDemographics(patient);

  const [showMissingDemographicsDialog, setShowMissingDemographicsDialog] =
    useState(isPatientMissingDemographics);

  const navigate = useNavigate();
  const refetchAndRedirect = () => {
    navigate(routeFns.patientEditDialogSuccess(formDraftId!, patientId!));
    refetchDraft && refetchDraft();
  };

  return (
    <Box>
      <MissingDemographicsDialog
        open={showMissingDemographicsDialog}
        onClose={() => {
          setShowMissingDemographicsDialog(false);
        }}
        restricted={patient.restricted}
      />
      <PatientCreateEdit
        existingPatient={patient}
        nhsNumber={patient.nhsNumber ?? undefined}
        refetchAndRedirect={refetchAndRedirect}
        setShowUpdateSuccessDialog={setShowUpdateSuccessDialog}
        mode={mode ?? "edit"}
        existingPatientId={patientId}
        expectedVersion={patient.version}
        exitPatientCreate={() => {
          if (patientId) {
            if (mode === "editPatientDialog") {
              navigate(routeFns.formDraftsComplete(formDraftId!, patientId!));
            } else if (redirectTo && isSoad) {
              navigate(redirectTo);
            } else {
              navigate(routeFns.patientHome(patientId));
            }
          }
        }}
        initialState={{
          givenName: patient.name.given,
          familyNameAndAddress: {
            name: patient.name.family,
            postalCode: patient.address.postalCode,
            address: patient.address.address,
            isConfirmed: true,
          },
          isPatientDateOfBirth: patient.dateOfBirth ? true : false,
          dateOfBirth: patient.dateOfBirth ?? null,
          deathNotificationStatus: patient.deathNotificationStatus,
          deceasedDateTime: patient.deceasedDateTime ?? null,
        }}
      />
    </Box>
  );
}

export function PatientEditPage({ mode, refetchDraft }: PatientEditProps) {
  const { patientId } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get("redirectTo");

  const [showUpdateSuccessDialog, setShowUpdateSuccessDialog] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <WithPatient patientId={patientId}>
        {(patient) => {
          return (
            <PatientEditInner
              patient={patient}
              mode={mode}
              refetchDraft={refetchDraft}
              setShowUpdateSuccessDialog={setShowUpdateSuccessDialog}
              redirectTo={redirectTo}
            />
          );
        }}
      </WithPatient>
      {showUpdateSuccessDialog && mode !== "editPatientDialog" && (
        <BannerDialog
          title={t("pages.patientEditCreate.updateSuccess")}
          message={t("pages.patientEditCreate.updateSuccessDialogMessage")}
          onClose={() => {
            if (searchParams.has("redirectTo")) {
              navigate(searchParams.get("redirectTo")!);
            } else {
              navigate(routeFns.patientHome(patientId!));
            }
          }}
        />
      )}
    </>
  );
}
